// API service for all backend calls
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Events API
export const eventsApi = {
  getAll: (params = {}) => api.get('/events', { params }),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  permanentDelete: (id) => api.delete(`/events/${id}/permanent`),
  restore: (id) => api.put(`/events/${id}/restore`),
  getInvitations: (id) => api.get(`/events/${id}/invitations`),
  syncInvitations: (id) => api.post(`/events/${id}/sync-invitations`),
  syncAllInvitations: () => api.post('/events/sync-all-invitations'),
  clearAllInvitations: () => api.delete('/invitations/clear-all')
};

// Evaluations API
export const evaluationsApi = {
  getCriteria: () => api.get('/evaluations/criteria'),
  getAll: (params = {}) => api.get('/evaluations', { params }),
  getMy: (params = {}) => api.get('/evaluations/my', { params }),
  create: (data) => api.post('/evaluations', data),
  delete: (id) => api.delete(`/evaluations/${id}`),
  exportPdf: async (evaluationId) => {
    const response = await api.get(`/export/evaluation/${evaluationId}/pdf`, { responseType: 'blob' });
    // Extract filename from Content-Disposition header, fallback to generic
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename=(.+)/);
    const filename = match ? match[1] : `Evaluation_${evaluationId}.pdf`;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// Stats API
export const statsApi = {
  getUserStats: (userId, period) => api.get(`/stats/user/${userId}`, { params: { period } }),
  getOverview: (period) => api.get('/stats/overview', { params: { period } }),
  getWorksPerMonth: (period) => api.get('/stats/works-per-month', { params: { period } })
};

// Users API
export const usersApi = {
  getAll: (role) => api.get('/users', { params: { role } }),
  updateRole: (userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } }),
  adminUpdate: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  getDevelopmentProfile: (userId) => api.get(`/users/${userId}/development-profile`)
};

// My Reflections API
export const reflectionsApi = {
  getMy: (period) => api.get('/my-reflections', { params: { period } })
};

// Awards API
export const awardsApi = {
  getAll: (params = {}) => api.get('/awards', { params }),
  getLeaderboard: (period) => api.get('/awards/leaderboard', { params: { period } }),
  create: (data) => api.post('/awards', data),
  update: (id, data) => api.put(`/awards/${id}`, data),
  delete: (id) => api.delete(`/awards/${id}`),
  autoCalculate: (year, quarter) => api.post(`/awards/auto-calculate?year=${year}&quarter=${quarter}`)
};

// Training Requests API
export const trainingApi = {
  getRequests: (period) => api.get('/training-requests', { params: { period } })
};

// Training Materials (Library) API
export const trainingMaterialsApi = {
  getAll: (params = {}) => api.get('/training/materials', { params }),
  upload: (formData) => api.post('/training/materials', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/training/materials/${id}`),
  download: (id) => api.get(`/training/materials/${id}/download`, { responseType: 'blob' })
};

// Training Workshops API
export const trainingWorkshopsApi = {
  getAll: () => api.get('/training/workshops'),
  create: (data) => api.post('/training/workshops', data),
  delete: (id) => api.delete(`/training/workshops/${id}`),
  getAttendance: (id) => api.get(`/training/workshops/${id}/attendance`),
  updateAttendance: (id, data) => api.post(`/training/workshops/${id}/attendance`, data),
  getParticipation: (year) => api.get('/training/participation', { params: { year } }),
  assign: (workshopId, payload) => api.post(`/training/workshops/${workshopId}/assign`, payload),
  unassign: (workshopId, userId) => api.delete(`/training/workshops/${workshopId}/assign/${userId}`),
  getMyAssignments: () => api.get('/training/assignments/me')
};

// Settings API
export const settingsApi = {
  getPeerEvaluation: () => api.get('/settings/peer-evaluation'),
  setPeerEvaluation: (enabled) => api.put(`/settings/peer-evaluation?enabled=${enabled}`)
};

// Calendar API
export const calendarApi = {
  getStatus: () => api.get('/calendar/status'),
  getAuthUrl: () => api.get('/calendar/auth-url'),
  authorize: (code) => api.post(`/calendar/authorize?code=${encodeURIComponent(code)}`)
};

// Helper to download blob as file
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Export API
export const exportApi = {
  toExcel: async (params = {}) => {
    const response = await api.get('/export/evaluations/excel', { params, responseType: 'blob' });
    downloadBlob(new Blob([response.data]), 'evaluations.xlsx');
  },
  toPdf: async (params = {}) => {
    const response = await api.get('/export/evaluations/pdf', { params, responseType: 'blob' });
    downloadBlob(new Blob([response.data], { type: 'application/pdf' }), 'evaluations.pdf');
  },
  monthlyReport: async (month, year) => {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await api.get('/export/monthly-report', { params, responseType: 'blob' });
    downloadBlob(new Blob([response.data], { type: 'application/pdf' }), 'monthly_report.pdf');
  },
  worksPerMonthPdf: async (period) => {
    const params = {};
    if (period) params.period = period;
    const response = await api.get('/export/works-per-month/pdf', { params, responseType: 'blob' });
    // Extract filename from Content-Disposition header or generate from period
    const disposition = response.headers['content-disposition'];
    let filename = 'works_per_month_report.pdf';
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) filename = match[1].replace(/['"]/g, '');
    }
    downloadBlob(new Blob([response.data], { type: 'application/pdf' }), filename);
  },
  worksPerMonthExcel: async (period) => {
    const params = {};
    if (period) params.period = period;
    const response = await api.get('/export/works-per-month/excel', { params, responseType: 'blob' });
    const disposition = response.headers['content-disposition'];
    let filename = 'works_per_month_report.xlsx';
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) filename = match[1].replace(/['"]/g, '');
    }
    downloadBlob(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
  }
};

export default api;
